/* eslint-disable node/no-process-env */
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { db } from "../storage/storage";
import { ensureNativeStorageResponses } from "../storage/NativeDB";
import { developCacheScope, rememberLegacyCacheOwner, selectDevelopCacheOwner } from "../storage/developCacheScope";
import { isRunningInIOSWebview, restApiUrl } from "../utils/environment";
import { replaceLocation } from "./replaceLocation";
import { markAutoSignInAttempt, safeReturnPath, shouldAttemptAutoSignIn } from "./autoSignIn";

const enabled =
  process.env.NEXT_PUBLIC_ENV === "development" && process.env.NEXT_PUBLIC_DEVELOP_IDEAFLOW_ID_ENABLED === "true";
const sessionKey = "develop-ideaflow-id-session";
const proofKey = "develop-ideaflow-id-proof";
// Set by an explicit sign-out: the next sign-in asks the provider for its
// account chooser (prompt=select_account) instead of reusing its session.
const chooseAccountKey = "develop-ideaflow-id-choose-account";
// Set by "Switch account": after the sign-out reload, sign-in starts straight
// away with the account chooser. Consumed once, so a cancel cannot loop.
const switchAccountKey = "develop-ideaflow-id-switch-account";
// Pending sign-in (same tab, like the proof): where to land afterwards, and
// whether it was an automatic prompt=none attempt whose failure stays silent.
const returnKey = "develop-ideaflow-id-return";
const silentKey = "develop-ideaflow-id-silent";
type Session = { token: string; userId: string; expiresAt: number };
type DevelopSignInError = "cancelled" | "failed";
type ContextValue = {
  session: Session | null;
  loading: boolean;
  error: DevelopSignInError | null;
  /** Where the /ideaflow-id return page sends the person once a sign-in came back. */
  next: string | null;
};
const Context = createContext<ContextValue>({ session: null, loading: false, error: null, next: null });

/** Fixed, readable copy for a sign-in that came back without a session. */
export function developSignInErrorMessage(error: DevelopSignInError): string {
  return error === "cancelled"
    ? "Sign-in was cancelled. You can try again."
    : "Ideaflow sign-in didn't work. Please try again. Your existing account and notes are unchanged.";
}

/** The iOS wrapper serves the app from thoughtstream-app:/// (native-ios/). */
function isIOSWrapperPage() {
  return typeof window !== "undefined" && window.location.protocol === "thoughtstream-app:";
}

/** The current path without the fragment or the iOS return marker. */
function cleanedLocation() {
  const query = new URLSearchParams(window.location.search);
  query.delete("ideaflow_return");
  const search = query.toString();
  return window.location.pathname + (search ? `?${search}` : "");
}

export function isDevelopIdeaflowIdEnabled() {
  return enabled;
}

/** Read and clear the pending sign-in's return path and silent flag. */
function takePendingReturn() {
  const silent = window.sessionStorage.getItem(silentKey) === "1";
  const returnPath = safeReturnPath(window.sessionStorage.getItem(returnKey));
  window.sessionStorage.removeItem(silentKey);
  window.sessionStorage.removeItem(returnKey);
  return { silent, returnPath };
}

/**
 * The original browser database has no owner-scoped folders or settings. Keep
 * it only when its notes identify this owner (or an explicit legacy link did).
 * A different owner gets another database on the next full page load; the old
 * cache, including unsynced edits and audio, stays intact for its owner.
 */
async function isolateCacheIfNeeded(owner: string, linkedOwner: string | null = null): Promise<boolean> {
  const scope = developCacheScope();
  const notes = await db.notes.getAll();
  if (isRunningInIOSWebview) {
    // Native SQLite has no per-owner database selector. Retain its existing
    // fail-closed behavior until the native storage supports isolated owners.
    if (notes.some((note) => note.authorId !== owner)) throw new Error("Native cache ownership mismatch");
    if (!linkedOwner && (await db.folders.getAll()).length && !(await db.users.get(owner)))
      throw new Error("Cached folders require a matching owner");
    return false;
  }
  if (scope === owner) {
    if (notes.some((note) => note.authorId !== owner)) throw new Error("Scoped cache ownership mismatch");
    return false;
  }
  if (!scope && (notes.length > 0 ? notes.every((note) => note.authorId === owner) : linkedOwner === owner))
    return false;
  const legacyOwner = notes[0]?.authorId;
  if (!scope && legacyOwner && notes.every((note) => note.authorId === legacyOwner))
    rememberLegacyCacheOwner(legacyOwner);
  return selectDevelopCacheOwner(owner);
}

export function DevelopSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<DevelopSignInError | null>(null);
  const [next, setNext] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    async function load() {
      // In the iOS wrapper the cached-data checks below read native storage
      // before the editor (and its native reply handler) has mounted.
      if (isRunningInIOSWebview) ensureNativeStorageResponses();
      let handingOff = false;
      let redirecting = false;
      let silent = false;
      let returnPath: string | null = null;
      let restored = false;
      let returned = false;
      let issuedToken: string | null = null;
      try {
        const fragment = new URLSearchParams(window.location.hash.slice(1));
        const ticket = fragment.get("ideaflow_ticket");
        const returnedError = fragment.get("ideaflow_error");
        if (!ticket && returnedError) {
          returned = true;
          window.history.replaceState(null, "", cleanedLocation());
          window.sessionStorage.removeItem(proofKey);
          window.sessionStorage.removeItem(proofKey + "-owner");
          ({ silent, returnPath } = takePendingReturn());
          // An automatic attempt that found no provider session (or failed in
          // any other way) just goes back to where the person was, signed out.
          if (silent || returnedError === "signed_out") {
            if (active) setNext(returnPath || "/");
          } else {
            const code = returnedError === "cancelled" ? "cancelled" : "failed";
            if (active) {
              setError(code);
              setNext(`/#ideaflow_error=${code}`);
            }
          }
        } else if (ticket) {
          handingOff = true;
          ({ silent, returnPath } = takePendingReturn());
          window.history.replaceState(null, "", cleanedLocation());
          const proof = window.sessionStorage.getItem(proofKey);
          window.sessionStorage.removeItem(proofKey);
          const expectedOwner = window.sessionStorage.getItem(proofKey + "-owner");
          window.sessionStorage.removeItem(proofKey + "-owner");
          if (!proof) throw new Error("Missing browser sign-in proof");
          const response = await fetch(`${restApiUrl}/ideaflow-id/exchange`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ticket, proof }),
          });
          if (!response.ok) throw new Error("Sign-in handoff failed");
          const value = await response.json();
          if (typeof value.token === "string") issuedToken = value.token;
          if (expectedOwner && value.userId !== expectedOwner) throw new Error("Identity ownership mismatch");
          if (typeof value.token !== "string" || !value.token.startsWith("ifid_") || typeof value.userId !== "string")
            throw new Error("Invalid session");
          const cacheChanged = await isolateCacheIfNeeded(value.userId, expectedOwner);
          const signedIn = { ...value, expiresAt: Date.now() + 3600000 };
          window.localStorage.setItem(sessionKey, JSON.stringify(signedIn));
          window.localStorage.removeItem(chooseAccountKey);
          if (cacheChanged) {
            // WebDB chooses its database at module load. Reload before the
            // editor can read the previous owner's cache.
            redirecting = true;
            replaceLocation(returnPath || "/");
            return;
          }
          if (active) {
            setSession(signedIn);
            setNext(returnPath || "/");
          }
        } else if (window.localStorage.getItem(switchAccountKey) === "1") {
          window.localStorage.removeItem(switchAccountKey);
          handingOff = true;
          redirecting = true;
          try {
            await startDevelopSignIn();
          } catch (failure) {
            redirecting = false;
            throw failure;
          }
        } else {
          const stored = window.localStorage.getItem(sessionKey);
          if (stored) {
            try {
              const value = JSON.parse(stored);
              if (
                typeof value.expiresAt !== "number" ||
                value.expiresAt <= Date.now() ||
                typeof value.token !== "string" ||
                !value.token.startsWith("ifid_") ||
                typeof value.userId !== "string"
              )
                throw new Error("Invalid stored session");
              if (await isolateCacheIfNeeded(value.userId)) {
                redirecting = true;
                replaceLocation(window.location.href);
                return;
              }
              restored = true;
              if (active) setSession(value);
            } catch (error) {
              window.localStorage.removeItem(sessionKey);
              throw error;
            }
          }
        }
      } catch (failure) {
        console.error("Develop Ideaflow ID sign-in failed", failure);
        // A session issued for a refused handoff is never used; end it.
        if (issuedToken && window.localStorage.getItem(sessionKey) === null)
          void fetch(`${restApiUrl}/ideaflow-id/revoke`, {
            method: "POST",
            headers: { Authorization: `Bearer ${issuedToken}` },
          }).catch(() => {});
        if (active && handingOff) {
          if (silent) setNext(returnPath || "/");
          else {
            setError("failed");
            setNext("/#ideaflow_error=failed");
          }
        }
      }
      // Signed out on an ordinary page load: try the provider session once per
      // browser session (prompt=none), unless a guard says not to.
      try {
        if (
          active &&
          !handingOff &&
          !returned &&
          !restored &&
          shouldAttemptAutoSignIn(chooseAccountKey) &&
          markAutoSignInAttempt()
        ) {
          redirecting = true;
          await startDevelopSignIn(undefined, { silent: true });
        }
      } catch (failure) {
        // Stay on the ordinary signed-out page; the marker stops a retry.
        redirecting = false;
        console.warn("Automatic Ideaflow sign-in skipped", failure);
        window.sessionStorage.removeItem(silentKey);
        window.sessionStorage.removeItem(returnKey);
      } finally {
        // While the browser leaves for the account chooser, keep "loading".
        if (active && !redirecting) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, []);
  return <Context.Provider value={{ session, loading, error, next }}>{children}</Context.Provider>;
}

export function useDevelopSession() {
  const { session, loading, error, next } = useContext(Context);
  if (!enabled) return { session: null, loading: false, error: null, next: null, logout: () => {} };
  return {
    session,
    loading,
    error,
    next,
    /** Sign out. With `switchAccount`, sign-in restarts with the account chooser. */
    logout: async (options?: unknown) => {
      const switchAccount = !!(options as { switchAccount?: boolean } | undefined)?.switchAccount;
      try {
        if (session)
          await fetch(`${restApiUrl}/ideaflow-id/revoke`, {
            method: "POST",
            headers: { Authorization: `Bearer ${session.token}` },
          });
      } finally {
        window.localStorage.removeItem(sessionKey);
        window.localStorage.setItem(chooseAccountKey, "1");
        if (switchAccount) window.localStorage.setItem(switchAccountKey, "1");
        window.location.assign("/");
      }
    },
  };
}

/**
 * Start Ideaflow ID sign-in. `silent` is the automatic attempt (prompt=none):
 * it replaces the current history entry and, if the provider has no session,
 * comes back to the same page signed out with no message.
 */
export async function startDevelopSignIn(legacy?: { token: string }, options: { silent?: boolean } = {}) {
  if (!enabled) throw new Error("Develop sign-in is disabled");
  const silent = !!options.silent;
  if (silent && (legacy || isIOSWrapperPage())) throw new Error("Automatic sign-in is web-only");
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const proof = Array.from(bytes, (v) => v.toString(16).padStart(2, "0")).join("");
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(proof));
  const challenge = Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join("");
  window.sessionStorage.setItem(proofKey, proof);
  window.sessionStorage.removeItem(proofKey + "-owner");
  // Come back to this exact page (path, query and hash) afterwards.
  const here = safeReturnPath(window.location.pathname + window.location.search + window.location.hash);
  if (here) window.sessionStorage.setItem(returnKey, here);
  else window.sessionStorage.removeItem(returnKey);
  if (silent) window.sessionStorage.setItem(silentKey, "1");
  else window.sessionStorage.removeItem(silentKey);
  // Ordinary sign-in sends no prompt (silent SSO); the first sign-in after an
  // explicit sign-out asks which account. The marker clears on success.
  const selectAccount = !silent && window.localStorage.getItem(chooseAccountKey) === "1";
  const response = await fetch(`${restApiUrl}/ideaflow-id/start`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(legacy ? { Authorization: `Bearer ${legacy.token}` } : {}),
    },
    body: JSON.stringify({
      challenge,
      link: !!legacy,
      selectAccount,
      ...(silent ? { silent: true } : {}),
      // The iOS wrapper hands this navigation to ASWebAuthenticationSession;
      // the server then returns the ticket to thoughtstream-app:///.
      ...(isIOSWrapperPage() ? { returnTo: "ios" } : {}),
    }),
  });
  if (!response.ok) throw new Error("Unable to start develop sign-in");
  const { authorizationUrl, ownerId } = await response.json();
  if (legacy && typeof ownerId !== "string") throw new Error("Invalid linked owner");
  if (!legacy && ownerId !== null) throw new Error("Unexpected linked owner");
  if (typeof ownerId === "string") window.sessionStorage.setItem(proofKey + "-owner", ownerId);
  const target = new URL(authorizationUrl);
  if (target.origin !== "https://id.ideaflow.app" || target.pathname !== "/api/auth/oauth2/authorize")
    throw new Error("Invalid authorization destination");
  if (silent) {
    // Never show the provider's sign-in page for an automatic attempt (an API
    // without prompt=none support, or its kill switch, stops here instead).
    if (target.searchParams.getAll("prompt").join(" ") !== "none") throw new Error("Silent sign-in unsupported");
    // Replace, so Back does not bounce through the hop.
    replaceLocation(target.toString());
    return;
  }
  window.location.assign(target.toString());
}
